import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  OrganizationSwitcher,
  UserButton,
  useAuth,
  useOrganization,
  useSession,
  useUser,
} from "@clerk/react";
import { Lock, Radio, Sparkles, X, RefreshCw, Globe2 } from "lucide-react";
import {
  planFromMetadata,
  startTeamCheckout,
  isPaidPlan,
  isPaidUserOrOrg,
  isClerkBillingPaid,
  syncClerkEntitlement,
  confirmCheckoutSession,
  fetchHubAccess,
  ensureOrgDomain,
  peekCheckoutIntent,
  clearCheckoutIntent,
  setCheckoutIntent,
} from "@/lib/billing";
import { brandedHubHost, HUB_APEX } from "@/lib/hosts";
import {
  orgProfileAppearance,
  orgSwitcherAppearance,
  userButtonAppearance,
  userProfileAppearance,
} from "@/lib/clerkAppearance";
import { LiveOpsSkeleton } from "@/components/LiveOpsSkeleton";
import {
  clearSaasCredentials,
  clearSessionForOrgSwitch,
  clearSessionForSignOut,
  postSignOutToOps,
  ONBOARD_DISMISSED_KEY,
} from "@/lib/sessionHygiene";

/**
 * Command = Live Ops (/ops/) on the org's SaaS hub.
 * Browser uses apex hub.opengateways.xyz + X-OG-Tenant (stable DNS).
 * Branded {slug}.hub is display/MCP when DNS works. Paid user is workspace admin.
 */
export function AppHome() {
  const { user, isLoaded } = useUser();
  const { session } = useSession();
  const { orgId, orgSlug, orgRole, isSignedIn, has } = useAuth();
  const { organization } = useOrganization();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const wasSignedInRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [paywallDismissed, setPaywallDismissed] = useState(false);
  const [hubToken, setHubToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("og_saas_hub_token");
    } catch {
      return null;
    }
  });
  const [hubUrl, setHubUrl] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("og_saas_hub_url");
    } catch {
      return null;
    }
  });
  /**
   * Optimistic sticky restore: if this org already has a session token,
   * start as "ok" so refresh does not flash a second full-screen boot.
   * connectHub verifies in the background and remints only if needed.
   */
  const [hubStatus, setHubStatus] = useState<"idle" | "connecting" | "ok" | "err">(
    () => {
      try {
        const t = sessionStorage.getItem("og_saas_hub_token");
        const h = sessionStorage.getItem("og_saas_hub_url");
        const oid = sessionStorage.getItem("og_saas_org_id");
        if (t && h && orgId && oid === orgId) return "ok";
        if (t && h && !orgId) return "ok"; // org still hydrating — keep calm
        return "idle";
      } catch {
        return "idle";
      }
    }
  );
  /** Live Ops iframe finished applying token + rooms (or timed out). */
  const [opsReady, setOpsReady] = useState(false);
  /** Single-flight + sticky connect — Clerk re-renders must not remint/remount. */
  const hubConnectInflight = useRef(false);
  const hubOkOrgRef = useRef<string | null>(
    (() => {
      try {
        const oid = sessionStorage.getItem("og_saas_org_id");
        const t = sessionStorage.getItem("og_saas_hub_token");
        return t && oid ? oid : null;
      } catch {
        return null;
      }
    })()
  );
  const lastPushedTokenRef = useRef<string>("");
  const hubStatusRef = useRef(hubStatus);
  hubStatusRef.current = hubStatus;
  /** Freeze /ops/? query once — mcp_hub/slug churn must not reload the iframe. */
  const opsSrcFrozenRef = useRef<string | null>(null);
  const opsReadyOrgRef = useRef<string | null>(null);

  const activeSlug =
    organization?.slug || orgSlug || (organization?.name ? organization.name : "");
  const displayHubHost = activeSlug
    ? brandedHubHost(activeSlug)
    : "hub.opengateways.xyz";

  const params = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const checkoutSuccess = params.get("checkout") === "success";
  const sessionId = params.get("session_id") || "";
  const upgradeTeam = params.get("upgrade") === "team";

  const userPlan = planFromMetadata(
    user?.publicMetadata as Record<string, unknown> | undefined
  );
  const orgPlan = planFromMetadata(
    organization?.publicMetadata as Record<string, unknown> | undefined
  );
  const plan = isPaidPlan(orgPlan) ? orgPlan : userPlan;
  const clerkPaid = isClerkBillingPaid(has);
  const metaPaid = isPaidUserOrOrg(
    user?.publicMetadata as Record<string, unknown> | undefined,
    organization?.publicMetadata as Record<string, unknown> | undefined
  );
  const paid = clerkPaid || metaPaid || confirmed;
  const planLabel =
    plan === "scale" ? "Enterprise" : paid ? "OG Hosted" : "Free";

  // When Clerk Billing unlocks paid but metadata lacks plan, stamp edge entitlement
  const entitlementSynced = useRef(false);
  useEffect(() => {
    if (!clerkPaid || metaPaid || !session?.id || !orgId) return;
    if (entitlementSynced.current) return;
    entitlementSynced.current = true;
    void syncClerkEntitlement({
      sessionId: session.id,
      clerkOrgId: organization?.id || orgId,
      plan: "hosted",
    }).then((r) => {
      if (r.ok) {
        void organization?.reload?.();
        void user?.reload?.();
      }
    });
  }, [clerkPaid, metaPaid, session?.id, orgId, organization, user]);

  const showPaywall = !paid && !confirming && !paywallDismissed;
  const [onboardDismissed, setOnboardDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(ONBOARD_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Stabilize iframe src: freeze identity once so Clerk reloads don't thrash Live Ops.
  const opsUserId = user?.id || "";
  const opsIdentityRef = useRef<{ name: string; email: string } | null>(null);
  if (opsUserId && !opsIdentityRef.current) {
    opsIdentityRef.current = {
      name:
        user?.fullName ||
        user?.firstName ||
        user?.primaryEmailAddress?.emailAddress ||
        "Admin",
      email: user?.primaryEmailAddress?.emailAddress || "",
    };
  }
  const opsUserName = opsIdentityRef.current?.name || "Admin";
  const opsUserEmail = opsIdentityRef.current?.email || "";
  // Browser always uses apex hub (stable DNS). Branded {slug}.hub is for agent MCP only.
  const apexHubUrl = `https://${HUB_APEX}`;
  // Keep iframe hub query stable at apex — hubUrl flips must not reload Live Ops
  const opsHub = apexHubUrl;
  const tenantSlug =
    (organization?.slug || orgSlug || "").replace(/[^a-z0-9-]/gi, "").toLowerCase() ||
    "";
  const mcpHubUrl = activeSlug
    ? `https://${brandedHubHost(activeSlug)}`
    : tenantSlug
      ? `https://${brandedHubHost(tenantSlug)}`
      : "";

  // Freeze iframe URL on first complete paint for this org.
  // Changing src (e.g. mcp_hub late-fill) reloads Live Ops → double loading screen.
  const opsSrc = useMemo(() => {
    if (opsSrcFrozenRef.current) return opsSrcFrozenRef.current;
    if (!opsUserId) return "/ops/?saas=1";
    const q = new URLSearchParams({
      saas: "1",
      name: opsUserName,
      email: opsUserEmail,
      role: "admin",
      uid: opsUserId,
    });
    q.set("hub", opsHub.replace(/\/$/, ""));
    // Prefer live slug, then sticky session (available on hard refresh)
    let slug = tenantSlug;
    if (!slug) {
      try {
        slug =
          (sessionStorage.getItem("og_saas_org_slug") || "")
            .replace(/[^a-z0-9-]/gi, "")
            .toLowerCase() || "";
      } catch {
        /* ok */
      }
    }
    if (slug) q.set("tenant", slug);
    // mcp_hub is pushed via postMessage — do not put it in the URL (avoids remount)
    const src = `/ops/?${q.toString()}`;
    // Freeze once we have uid + (slug or paid shell is up). Remount only on org key change.
    if (opsUserId) opsSrcFrozenRef.current = src;
    return src;
  }, [opsUserId, opsUserName, opsUserEmail, opsHub, tenantSlug]);

  const pushTokenToOps = useCallback(
    (token: string, hub?: string | null, opts?: { force?: boolean; orgSlug?: string | null }) => {
      if (!token) return;
      const cleanHub = (hub || "").replace(/\/$/, "") || undefined;
      const slug =
        (opts?.orgSlug || tenantSlug || "").replace(/[^a-z0-9-]/gi, "").toLowerCase() ||
        "";
      const mcp =
        (slug ? `https://${brandedHubHost(slug)}` : "") ||
        mcpHubUrl ||
        sessionStorage.getItem("og_saas_mcp_hub_url") ||
        "";
      if (cleanHub) {
        sessionStorage.setItem("og_saas_hub_url", cleanHub);
      }
      if (mcp) {
        sessionStorage.setItem("og_saas_mcp_hub_url", mcp.replace(/\/$/, ""));
      }
      if (slug) {
        sessionStorage.setItem("og_saas_org_slug", slug);
      }
      if (orgId) {
        sessionStorage.setItem("og_saas_org_id", orgId);
      }
      sessionStorage.setItem("og_saas_hub_token", token);
      // Skip spam postMessage when token unchanged (stops iframe re-apply thrash)
      const fingerprint = `${token}|${cleanHub || sessionStorage.getItem("og_saas_hub_url") || ""}|${slug}|${mcp}`;
      if (!opts?.force && lastPushedTokenRef.current === fingerprint) return;
      lastPushedTokenRef.current = fingerprint;

      const payload = {
        type: "og-saas-auth" as const,
        token,
        hub_url:
          cleanHub || sessionStorage.getItem("og_saas_hub_url") || undefined,
        /** Branded hub for agent MCP OPENGATEWAY_URL snippets */
        mcp_hub_url:
          mcp.replace(/\/$/, "") ||
          sessionStorage.getItem("og_saas_mcp_hub_url") ||
          undefined,
        org_slug: slug || sessionStorage.getItem("og_saas_org_slug") || undefined,
      };
      const send = () => {
        try {
          iframeRef.current?.contentWindow?.postMessage(
            payload,
            window.location.origin
          );
        } catch {
          /* ignore */
        }
      };
      send();
      // Two short retries only — enough for slow iframe boot, not a refresh loop
      window.setTimeout(send, 300);
      window.setTimeout(send, 1200);
    },
    [tenantSlug, mcpHubUrl, orgId]
  );

  // Cooldown for auth-failed remints (stops connect thrash → iframe reload feel)
  const authFailAtRef = useRef(0);

  // When token arrives after iframe already asked, push immediately
  useEffect(() => {
    if (hubToken) pushTokenToOps(hubToken, hubUrl, { orgSlug: tenantSlug });
  }, [hubToken, hubUrl, tenantSlug, pushTokenToOps]);

  /** Probe rooms against apex (or given hub) with tenant header — never requires branded DNS. */
  const probeRooms = useCallback(
    async (hubBase: string, token: string, slug?: string | null) => {
      const base = hubBase.replace(/\/$/, "");
      const s = (slug || "").replace(/[^a-z0-9-]/gi, "").toLowerCase();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      if (s) headers["X-OG-Tenant"] = s;
      const pr = await fetch(`${base}/v1/rooms`, {
        headers,
        credentials: "omit",
        mode: "cors",
      });
      return pr;
    },
    []
  );

  const connectHub = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!user || !session?.id || !paid || !orgId) return;
      const slug = organization?.slug || orgSlug || undefined;
      const apex = apexHubUrl.replace(/\/$/, "");
      const storedHub = (
        hubUrl ||
        sessionStorage.getItem("og_saas_hub_url") ||
        ""
      ).replace(/\/$/, "");
      const storedTok =
        hubToken || sessionStorage.getItem("og_saas_hub_token") || "";
      const storedSlug =
        sessionStorage.getItem("og_saas_org_slug") || slug || "";
      // Sticky if we already connected this org and apex token still lists rooms
      if (
        !opts?.force &&
        hubOkOrgRef.current === orgId &&
        hubStatusRef.current === "ok" &&
        storedTok &&
        storedHub &&
        (!slug || !storedSlug || storedSlug === slug)
      ) {
        try {
          const pr = await probeRooms(storedHub, storedTok, slug || storedSlug);
          if (pr.ok) {
            pushTokenToOps(storedTok, storedHub, {
              force: true,
              orgSlug: slug || storedSlug,
            });
            return;
          }
        } catch {
          /* remint below */
        }
        // fall through to remint
      }
      if (hubConnectInflight.current) return;
      hubConnectInflight.current = true;
      // Only paint connecting chrome on cold start / explicit reconnect
      if (hubStatusRef.current !== "ok" || opts?.force) {
        setHubStatus("connecting");
      }
      try {
        // Still attach branded domain for MCP agents (best-effort; not required for browser)
        if (slug && session?.id) {
          try {
            await ensureOrgDomain(slug, session.id);
          } catch {
            /* hub-access still retries domain */
          }
        }
        const access = await fetchHubAccess({
          sessionId: session.id,
          clerkUserId: user.id,
          orgId,
          orgSlug: slug,
        });
        // Prefer apex from edge; fall back if older edge still returns branded
        const accessSlug = access.org_slug || slug || "";
        let hubBase = access.hub_url.replace(/\/$/, "");
        if (
          accessSlug &&
          /https?:\/\/[^/]+\.hub\./i.test(hubBase) &&
          !/https?:\/\/hub\./i.test(hubBase)
        ) {
          hubBase = apex;
        }
        let roomsOk = false;
        let lastRoomsStatus = 0;
        for (const wait of [0, 1500, 3000]) {
          if (wait) await new Promise((r) => setTimeout(r, wait));
          try {
            const pr = await probeRooms(hubBase, access.token, accessSlug);
            lastRoomsStatus = pr.status;
            if (pr.ok) {
              roomsOk = true;
              break;
            }
            if (pr.status !== 401 && pr.status !== 503) break;
          } catch {
            /* retry — often DNS on old branded path */
          }
        }
        if (!roomsOk) {
          throw new Error(
            `hub_token_unusable (rooms HTTP ${lastRoomsStatus || "error"}) — gateway not ready. Retry Reconnect hub.`
          );
        }
        setHubToken(access.token);
        setHubUrl(hubBase);
        pushTokenToOps(access.token, hubBase, {
          force: true,
          orgSlug: accessSlug,
        });
        hubOkOrgRef.current = orgId;
        setHubStatus("ok");
        // Surface domain warnings without blocking Live Ops (wildcard routes work)
        if (access.domain_ready === false && access.domain_detail) {
          console.warn(
            "[command] hub domain not fully ready:",
            access.domain_detail
          );
        }
        setErr(null);
      } catch (e) {
        // Do not wipe a still-valid sticky token on a transient hub blip —
        // clearing sessionStorage while Live Ops React state stays "authed"
        // caused 401 on Agent token Revoke. Only drop sticky after hard fail
        // when we had no prior ok session for this org.
        hubOkOrgRef.current = null;
        const hadOk = hubStatusRef.current === "ok";
        if (!hadOk || opts?.force) {
          clearSaasCredentials();
          setHubToken(null);
          lastPushedTokenRef.current = "";
        }
        if (hubStatusRef.current !== "ok" || opts?.force) {
          setHubStatus("err");
        }
        setErr(e instanceof Error ? e.message : "hub_access_failed");
      } finally {
        hubConnectInflight.current = false;
      }
    },
    [
      user,
      session?.id,
      paid,
      orgId,
      orgSlug,
      organization?.slug,
      hubToken,
      hubUrl,
      apexHubUrl,
      probeRooms,
      pushTokenToOps,
    ]
  );

  // Ops iframe ↔ Command: token request + 401 remint + ready signal
  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as { type?: string };
      if (d?.type === "og-saas-ops-ready") {
        setOpsReady(true);
        opsReadyOrgRef.current = orgId || null;
        return;
      }
      if (d?.type === "og-saas-auth-request") {
        const token =
          hubToken || sessionStorage.getItem("og_saas_hub_token") || "";
        const hub =
          hubUrl || sessionStorage.getItem("og_saas_hub_url") || null;
        if (token)
          pushTokenToOps(token, hub, {
            force: true,
            orgSlug: tenantSlug || sessionStorage.getItem("og_saas_org_slug"),
          });
        else void connectHub({ force: true });
      }
      if (d?.type === "og-saas-auth-failed") {
        setOpsReady(false);
        const now = Date.now();
        if (now - authFailAtRef.current < 8000) return; // debounce remint storm
        authFailAtRef.current = now;
        void connectHub({ force: true });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [hubToken, hubUrl, tenantSlug, pushTokenToOps, connectHub, orgId]);

  // Safety: never trap under skeleton if Live Ops forgets to signal
  useEffect(() => {
    if (hubStatus !== "ok" || opsReady) return;
    // Sticky refresh is usually fast; cold hub may need a beat for postMessage
    const ms = hubToken ? 1800 : 3200;
    const t = window.setTimeout(() => setOpsReady(true), ms);
    return () => window.clearTimeout(t);
  }, [hubStatus, opsReady, hubToken]);

  // Connect once per org — do NOT re-run when connectHub identity churns
  useEffect(() => {
    if (!paid || !orgId) return;
    if (hubOkOrgRef.current === orgId && hubStatusRef.current === "ok") return;
    void connectHub();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: org/paid only
  }, [paid, orgId]);

  // Auto-retry hub connect on error (cold start / domain race after paid signup)
  useEffect(() => {
    if (!paid || !orgId || hubStatus !== "err") return;
    let cancelled = false;
    const delays = [3000, 8000, 15000];
    const timers = delays.map((ms, i) =>
      window.setTimeout(() => {
        if (cancelled) return;
        if (hubStatusRef.current === "ok") return;
        void connectHub({ force: true });
      }, ms + i)
    );
    return () => {
      cancelled = true;
      for (const t of timers) window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid, orgId, hubStatus]);

  // Org switch: wipe credentials so iframe cannot re-apply previous org token
  useEffect(() => {
    if (!orgId) return;
    if (hubOkOrgRef.current && hubOkOrgRef.current !== orgId) {
      clearSessionForOrgSwitch();
      hubOkOrgRef.current = null;
      lastPushedTokenRef.current = "";
      opsSrcFrozenRef.current = null;
      opsReadyOrgRef.current = null;
      setOpsReady(false);
      setHubStatus("idle");
      setHubToken(null);
      setHubUrl(null);
      setErr(null);
      if (paid) void connectHub({ force: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- orgId identity only
  }, [orgId]);

  // Clerk sign-out / account switch: wipe hub + local auth crumbs
  useEffect(() => {
    if (wasSignedInRef.current && isSignedIn === false) {
      postSignOutToOps(iframeRef.current);
      clearSessionForSignOut();
      hubOkOrgRef.current = null;
      lastPushedTokenRef.current = "";
      setHubToken(null);
      setHubUrl(null);
      setHubStatus("idle");
    }
    wasSignedInRef.current = Boolean(isSignedIn);
  }, [isSignedIn]);

  // Provision domain as soon as we have a slug + session (even pre-pay)
  useEffect(() => {
    if (!activeSlug || !session?.id) return;
    void ensureOrgDomain(activeSlug, session.id);
  }, [activeSlug, session?.id]);

  const runConfirm = useCallback(async () => {
    if (!user) return;
    const sid =
      sessionId ||
      new URLSearchParams(window.location.search).get("session_id") ||
      "";
    if (!sid.startsWith("cs_")) {
      setErr(
        "Missing Stripe session id. Use Retry from a Checkout success URL, or re-subscribe once."
      );
      return;
    }
    setConfirming(true);
    setErr(null);
    try {
      await confirmCheckoutSession({
        sessionId: sid,
        clerkUserId: user.id,
        clerkOrgId: orgId,
      });
      try {
        await user.reload();
      } catch {
        /* ok */
      }
      try {
        // Org plan is what gates hub-access — must refresh after Stripe stamp
        await organization?.reload?.();
      } catch {
        /* ok */
      }
      setConfirmed(true);
      setPaywallDismissed(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.pathname + url.search);
      // Mission-critical: force hub provision after paid checkout
      hubOkOrgRef.current = null;
      await connectHub({ force: true });
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Could not activate plan — try Retry"
      );
    } finally {
      setConfirming(false);
    }
  }, [user, sessionId, orgId, organization, connectHub]);

  // Auto-checkout after sign-up / org create when user intended Hosted (once)
  const autoCheckoutTried = useRef(false);
  /** After Stripe return — connect hub once even if plan already set by webhook */
  const checkoutHubTried = useRef(false);
  /** Stamp entitlement + stripe_customer_id once per Checkout return */
  const confirmTried = useRef(false);

  useEffect(() => {
    if (!isLoaded || !user || !checkoutSuccess) return;
    // Always run confirm when session_id present — stamps stripe_customer_id even
    // if webhook already set plan=hosted (otherwise Billing portal has no cus_).
    if (sessionId.startsWith("cs_")) {
      if (confirmTried.current) return;
      confirmTried.current = true;
      void runConfirm();
      return;
    }
    if (isPaidPlan(plan)) {
      setConfirmed(true);
      if (!checkoutHubTried.current) {
        checkoutHubTried.current = true;
        hubOkOrgRef.current = null;
        void connectHub({ force: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per checkout return
  }, [isLoaded, user, checkoutSuccess, plan, sessionId, runConfirm]);
  useEffect(() => {
    if (!isLoaded || !user || paid || !orgId) return;
    const intent = upgradeTeam || peekCheckoutIntent() === "team";
    if (!intent || autoCheckoutTried.current) return;
    autoCheckoutTried.current = true;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr(null);
      try {
        if (activeSlug && session?.id) {
          await ensureOrgDomain(activeSlug, session.id);
        }
        const { url } = await startTeamCheckout({
          customerEmail: user.primaryEmailAddress?.emailAddress,
          clientReferenceId: user.id,
          clerkOrgId: orgId,
        });
        clearCheckoutIntent();
        // Drop upgrade query so back-nav doesn't re-open Stripe
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete("upgrade");
          window.history.replaceState({}, "", u.pathname + u.search);
        } catch {
          /* ok */
        }
        if (!cancelled) window.location.href = url;
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : String(e));
          setBusy(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, user, upgradeTeam, paid, orgId, activeSlug, session?.id]);

  const startCheckout = useCallback(async () => {
    if (!user) return;
    if (!orgId) {
      setCheckoutIntent("team");
      setErr("Create your organization first, then subscribe.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (activeSlug && session?.id) {
        await ensureOrgDomain(activeSlug, session.id);
      }
      const { url } = await startTeamCheckout({
        customerEmail: user.primaryEmailAddress?.emailAddress,
        clientReferenceId: user.id,
        clerkOrgId: orgId,
      });
      clearCheckoutIntent();
      window.location.href = url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [user, orgId, activeSlug, session?.id]);

  // Shell-first: never full-screen boot after Clerk/org are ready.
  // Header always paints; Live Ops area shows skeleton until iframe is ready.
  const showOpsSkeleton =
    paid &&
    (hubStatus === "err" ||
      hubStatus === "connecting" ||
      hubStatus === "idle" ||
      (hubStatus === "ok" && !opsReady));
  const opsInteractive = paid && hubStatus === "ok" && opsReady && !showPaywall;

  return (
    <div className="relative flex h-[100dvh] flex-col overflow-hidden bg-og-ink text-zinc-100">
      <header className="z-30 flex h-12 shrink-0 items-center justify-between border-b border-white/10 bg-zinc-950/95 px-3 backdrop-blur-xl md:px-4">
        <div className="flex items-center gap-2.5">
          <img
            src="/og-logo.png"
            alt=""
            className="h-7 w-7 rounded-lg shadow shadow-orange-500/30"
            width={28}
            height={28}
          />
          <div className="leading-tight">
            <p className="text-sm font-semibold">Command</p>
            <p className="max-w-[10rem] truncate text-[10px] uppercase tracking-wider text-zinc-500 sm:max-w-none">
              {organization?.name || "Live Ops"}
              {orgRole === "org:admin" ? " · admin" : ""}
            </p>
          </div>
          <span
            className={`ml-2 text-[11px] font-medium tabular-nums ${
              paid
                ? "text-emerald-400/90"
                : confirming
                  ? "text-orange-400/90"
                  : "text-zinc-500"
            }`}
          >
            {confirming ? "Activating…" : planLabel}
          </span>
          {paid ? (
            <span
              className={`hidden items-center gap-1.5 text-[11px] sm:inline-flex ${
                hubStatus === "err" ? "text-red-400/90" : "text-zinc-500"
              }`}
              title={
                hubStatus === "err"
                  ? "Hub connection error — use Reconnect"
                  : "Your private hub host"
              }
            >
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                  hubStatus === "ok"
                    ? "bg-emerald-500"
                    : hubStatus === "err"
                      ? "bg-red-400"
                      : "bg-zinc-500"
                }`}
                aria-hidden
              />
              {/* Always show host — never flash "Connecting…" (backend work is silent) */}
              <span className="max-w-[14rem] truncate font-mono text-[10px] tracking-tight">
                {displayHubHost}
              </span>
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {!paid && !confirming ? (
            <button
              type="button"
              onClick={() => setPaywallDismissed(false)}
              className="hidden text-xs font-medium text-orange-400 hover:text-orange-300 sm:inline"
            >
              Upgrade
            </button>
          ) : null}
          {paid && hubStatus === "err" ? (
            <button
              type="button"
              onClick={() => void connectHub({ force: true })}
              className="hidden text-xs text-orange-400 hover:text-orange-300 sm:inline"
            >
              Reconnect hub
            </button>
          ) : null}
          {/* Invites: Clerk OrganizationProfile → Members (not a custom bar) */}
          <OrganizationSwitcher
            hidePersonal
            afterCreateOrganizationUrl="/"
            afterSelectOrganizationUrl="/"
            createOrganizationMode="modal"
            organizationProfileMode="modal"
            appearance={orgSwitcherAppearance}
            organizationProfileProps={{
              appearance: orgProfileAppearance,
            }}
          />
          <UserButton
            appearance={userButtonAppearance}
            userProfileProps={{
              appearance: userProfileAppearance,
            }}
          />
        </div>
      </header>

      {err ? (
        <div className="z-30 flex flex-wrap items-center justify-center gap-3 border-b border-red-500/30 bg-red-500/10 px-4 py-2 text-center text-xs text-red-200">
          <span>{err}</span>
          {checkoutSuccess || sessionId ? (
            <button
              type="button"
              onClick={() => void runConfirm()}
              className="inline-flex items-center gap-1 rounded-full border border-red-400/40 px-2.5 py-0.5 font-medium hover:bg-red-500/20"
            >
              <RefreshCw className="h-3 w-3" />
              Retry activation
            </button>
          ) : null}
        </div>
      ) : null}

      {confirming ? (
        <div className="z-30 border-b border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-center text-xs text-emerald-200">
          Payment received — activating Hosted plan for your organization…
        </div>
      ) : null}

      {paid && hubStatus === "ok" && opsReady && !onboardDismissed ? (
        <div className="z-30 border-b border-orange-500/25 bg-zinc-950/95 px-4 py-3 text-sm text-zinc-200">
          <div className="mx-auto flex max-w-4xl flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <p className="font-semibold text-white">
                Your gateway is live
              </p>
              <p className="text-xs text-zinc-400">
                Hub:{" "}
                <span className="font-mono text-orange-300">
                  {displayHubHost}
                </span>
                {" · "}
                Mint agent keys in Live Ops → Agent tokens, then paste into Grok /
                Claude / Cursor MCP with{" "}
                <code className="text-[10px] text-zinc-300">
                  OPENGATEWAY_URL=https://{displayHubHost}
                </code>
                . Agents stay listening after join_room.
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 text-xs text-zinc-500 hover:text-white"
              onClick={() => {
                setOnboardDismissed(true);
                try {
                  sessionStorage.setItem(ONBOARD_DISMISSED_KEY, "1");
                } catch {
                  /* ok */
                }
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <div className="relative min-h-0 flex-1 bg-zinc-100 dark:bg-zinc-950">
        {/* Mount iframe early so it boots under the skeleton (no full-screen splash). */}
        {paid ? (
          <iframe
            key={orgId || "no-org"}
            ref={iframeRef}
            title="OpenGateway Live Ops"
            src={opsSrc}
            className={`absolute inset-0 h-full w-full border-0 bg-zinc-100 transition-opacity duration-300 ease-out dark:bg-zinc-950 ${
              opsInteractive
                ? "z-[5] opacity-100"
                : "pointer-events-none z-0 select-none opacity-0"
            }`}
            allow="clipboard-read; clipboard-write"
            onLoad={() => {
              const token =
                hubToken || sessionStorage.getItem("og_saas_hub_token") || "";
              const hub =
                hubUrl || sessionStorage.getItem("og_saas_hub_url") || null;
              if (token) {
                pushTokenToOps(token, hub, {
                  force: true,
                  orgSlug:
                    tenantSlug || sessionStorage.getItem("og_saas_org_slug"),
                });
              } else if (hubStatusRef.current !== "ok") {
                void connectHub();
              }
            }}
          />
        ) : null}

        {/* In-content skeleton — Command chrome already visible above */}
        {showOpsSkeleton ? (
          <LiveOpsSkeleton
            host={displayHubHost}
            error={hubStatus === "err"}
            onRetry={
              hubStatus === "err"
                ? () => void connectHub({ force: true })
                : undefined
            }
          />
        ) : null}

        {showPaywall ? (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
            <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950 p-6 shadow-2xl shadow-orange-500/20 sm:p-8">
              <button
                type="button"
                onClick={() => setPaywallDismissed(true)}
                className="absolute right-3 top-3 rounded-full p-1.5 text-zinc-500 hover:bg-white/5 hover:text-white"
                aria-label="Peek at app"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500/15 text-orange-400">
                <Lock className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-orange-400/90">
                OG Hosted · $9.99/mo
              </p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight text-white">
                Unlock Live Ops
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                Subscribe and your org becomes workspace admin on your branded
                multi-agent hub — no OSS “create admin” step.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2">
                  <Globe2 className="h-4 w-4 text-orange-400" />
                  Gateway at{" "}
                  <span className="font-medium text-orange-200/90">
                    {displayHubHost}
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Radio className="h-4 w-4 text-orange-400" />
                  Rooms, presence, tasks, @all
                </li>
                <li className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-orange-400" />
                  Agent keys for every harness
                </li>
              </ul>
              <button
                type="button"
                disabled={busy}
                onClick={() => void startCheckout()}
                className="mt-6 w-full rounded-full bg-gradient-to-b from-orange-400 to-orange-600 py-3 text-sm font-semibold text-orange-950 shadow-lg shadow-orange-500/30 hover:brightness-110 disabled:opacity-60"
              >
                {busy ? "Opening checkout…" : "Subscribe — $9.99/mo"}
              </button>
              <p className="mt-2 text-center text-[11px] text-zinc-500">
                Manage plan later via Organization switcher → Manage organization
                (Clerk Billing).
              </p>
              {checkoutSuccess ? (
                <button
                  type="button"
                  onClick={() => void runConfirm()}
                  className="mt-3 w-full text-center text-xs text-orange-300 hover:underline"
                >
                  Already paid? Retry activation
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
